// Search View

import { search, type SearchType } from '../api/truths';
import type { SearchResult } from '../api/types';
import { navigate } from '../router';
import { icon } from '../icons';

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export async function createSearchView(): Promise<HTMLElement> {
  const view = document.createElement('div');
  view.className = 'search-view';

  let currentType: SearchType = 'all';
  let currentQuery = '';

  // Search input
  const searchBox = document.createElement('div');
  searchBox.className = 'search';
  searchBox.innerHTML = `
    <input
      type="text"
      class="search__input"
      placeholder="Search truths and replies..."
      autofocus
    />
    <div class="search__filters">
      <button class="feed-tab feed-tab--active" data-type="all">All</button>
      <button class="feed-tab" data-type="truths">Truths</button>
      <button class="feed-tab" data-type="replies">Replies</button>
    </div>
  `;
  view.appendChild(searchBox);

  // Results container
  const resultsContainer = document.createElement('div');
  resultsContainer.className = 'search-results';
  view.appendChild(resultsContainer);

  const input = searchBox.querySelector('.search__input') as HTMLInputElement;
  const filterBtns = searchBox.querySelectorAll('.feed-tab');

  // Filter button handlers
  filterBtns.forEach((btn) => {
    btn.addEventListener('click', async () => {
      filterBtns.forEach((b) => b.classList.remove('feed-tab--active'));
      btn.classList.add('feed-tab--active');
      currentType = (btn as HTMLElement).dataset.type as SearchType;

      if (currentQuery) {
        await performSearch(currentQuery, currentType, resultsContainer);
      }
    });
  });

  // Search on input (debounced)
  let debounceTimer: ReturnType<typeof setTimeout>;
  input.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      currentQuery = input.value.trim();
      if (currentQuery.length >= 2) {
        await performSearch(currentQuery, currentType, resultsContainer);
      } else {
        resultsContainer.innerHTML = `
          <div class="empty">
            <div class="empty__icon">${icon('search', 48)}</div>
            <p class="empty__text">Enter at least 2 characters</p>
          </div>
        `;
      }
    }, 300);
  });

  // Initial state
  resultsContainer.innerHTML = `
    <div class="empty">
      <div class="empty__icon">${icon('search', 48)}</div>
      <p class="empty__text">Search truths</p>
      <p style="color: var(--text-muted); font-size: 0.85em; margin-top: 8px;">
        Semantic similarity search
      </p>
    </div>
  `;

  // Focus input
  setTimeout(() => input.focus(), 100);

  return view;
}

async function performSearch(query: string, type: SearchType, container: HTMLElement): Promise<void> {
  container.innerHTML = `
    <div class="loading">
      <div class="loading__spinner"></div>
      <p>Searching...</p>
    </div>
  `;

  try {
    const response = await search(query, type, 30);
    const results = response.results || [];

    if (results.length === 0) {
      container.innerHTML = `
        <div class="empty">
          <div class="empty__icon">${icon('crab', 48)}</div>
          <p class="empty__text">No results for "${escapeHtml(query)}"</p>
          <p style="color: var(--text-muted); font-size: 0.85em; margin-top: 8px;">
            Try different keywords
          </p>
        </div>
      `;
      return;
    }

    container.innerHTML = `
      <div style="color: var(--text-muted); margin-bottom: 15px; font-size: 0.9em;">
        Found ${results.length} result${results.length === 1 ? '' : 's'} for "${escapeHtml(query)}"
      </div>
    `;

    results.forEach((result: SearchResult) => {
      const item = document.createElement('div');
      item.className = 'search__result truth-card';
      item.style.cursor = 'pointer';

      const authorName = result.author?.name || 'Unknown';
      const similarityPercent = Math.round(result.similarity * 100);

      const typeLabel = result.type === 'reply' ? `${icon('comment', 12)} Reply` : `${icon('megaphone', 12)} Truth`;
      const targetId = result.type === 'reply' ? result.truth_id : result.id;

      item.innerHTML = `
        <div class="search__result-score">${typeLabel} • ${similarityPercent}% match</div>
        <div class="truth-card__header">
          <a href="#/@${encodeURIComponent(authorName)}" class="truth-card__author" onclick="event.stopPropagation()">
            @${escapeHtml(authorName)}
          </a>
        </div>
        <div class="truth-card__content">${escapeHtml(result.content)}</div>
      `;

      item.addEventListener('click', () => {
        navigate(`/truth/${targetId}`);
      });

      container.appendChild(item);
    });
  } catch (err) {
    console.error('Search failed:', err);
    container.innerHTML = `
      <div class="empty">
        <div class="empty__icon">${icon('error', 48)}</div>
        <p class="empty__text">Search failed</p>
      </div>
    `;
  }
}
